/**
 * Jul-2026 (User-Wunsch) — Rechnung per E-Mail mit zusätzlichen Anhängen
 * (Fotos + Dateien). Anhänge werden am Fahrzeug/Kunden archiviert und der
 * Kunde erhält zusätzlich Download-Links (1 Jahr gültig).
 */
import { useState, useRef } from "react";
import { toast } from "sonner";
import { X, Send, Paperclip, Loader2, FileText, Image as ImageIcon, Trash2 } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";

const MAX_FILES = 8;
const MAX_MB = 10;

export default function SendInvoiceEmailModal({ invoice, customer, onClose, onSent }) {
    const [toEmail, setToEmail] = useState(invoice.customer_email || customer?.email || "");
    const [message, setMessage] = useState("");
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const addFiles = (list) => {
        const incoming = Array.from(list || []);
        const ok = [];
        for (const f of incoming) {
            if (f.size > MAX_MB * 1024 * 1024) { toast.error(`${f.name}: zu groß (max ${MAX_MB} MB)`); continue; }
            ok.push(f);
        }
        setFiles((prev) => [...prev, ...ok].slice(0, MAX_FILES));
    };

    const send = async () => {
        const to = toEmail.trim().toLowerCase();
        if (!to) { toast.error("Bitte E-Mail-Adresse eingeben"); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("to_email", to);
            if (message.trim()) fd.append("message", message.trim());
            files.forEach((f) => fd.append("files", f, f.name));
            const res = await api.post(`/invoices/${invoice.id}/send-email`, fd, {
                headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
            });
            const tag = res.data?.queued_for_approval
                ? " — wartet in der E-Mail-Freigabe (Postausgang)"
                : (res.data?.mocked ? " (Mock — Mail-Versand noch nicht live)" : "");
            const att = res.data?.attachments_count > 0 ? ` · ${res.data.attachments_count} Anhang/Anhänge archiviert` : "";
            toast.success(`E-Mail an ${res.data?.sent_to}${res.data?.queued_for_approval ? " vorbereitet" : " gesendet"}${tag}${att}`);
            onSent?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
        }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="invoice-email-modal">
            <div className="bg-white max-w-lg w-full rounded-sm shadow-xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 bg-[#111827] text-white rounded-t-sm">
                    <div className="font-display font-black text-lg">Rechnung {invoice.invoice_number} per E-Mail</div>
                    <button onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="invoice-email-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5 space-y-3 overflow-y-auto">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Empfänger (E-Mail)</label>
                        <Input value={toEmail} onChange={(e) => setToEmail(e.target.value)} type="email"
                            placeholder="kunde@example.de" className="rounded-sm mt-1" data-testid="invoice-email-to" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Persönliche Nachricht (optional)</label>
                        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                            placeholder="Ersetzt den Standard-Begleittext…" className="rounded-sm mt-1" data-testid="invoice-email-message" />
                    </div>
                    <div>
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Anhänge (Fotos & Dateien)</label>
                            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                                disabled={files.length >= MAX_FILES} className="h-7 text-[11px] rounded-sm" data-testid="invoice-email-add-files">
                                <Paperclip className="w-3 h-3 mr-1" /> Datei/Foto hinzufügen
                            </Button>
                            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                                className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                                data-testid="invoice-email-file-input" />
                        </div>
                        {files.length > 0 && (
                            <ul className="mt-2 border border-gray-200 rounded-sm divide-y" data-testid="invoice-email-file-list">
                                {files.map((f, i) => (
                                    <li key={i} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-xs">
                                        <span className="inline-flex items-center gap-1.5 min-w-0">
                                            {f.type.startsWith("image/") ? <ImageIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" /> : <FileText className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                                            <span className="truncate">{f.name}</span>
                                            <span className="text-gray-400">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
                                        </span>
                                        <button type="button" onClick={() => setFiles((prev) => prev.filter((_, x) => x !== i))}
                                            className="text-red-500 hover:text-red-700 shrink-0" data-testid={`invoice-email-remove-file-${i}`}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        <p className="text-[11px] text-gray-500 mt-1.5">
                            Anhänge werden automatisch <b>am Fahrzeug/Kunden archiviert</b> — der Kunde erhält sie als
                            E-Mail-Anhang <b>plus Download-Link</b> (1 Jahr abrufbar). Die Rechnung (PDF) und ggf. die
                            XRechnung werden immer mitgesendet.
                        </p>
                    </div>
                </div>
                <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose} className="rounded-sm text-[12px] font-bold" data-testid="invoice-email-cancel">Abbrechen</Button>
                    <Button onClick={send} disabled={busy} className="rounded-sm text-[12px] font-black bg-[#111827] hover:bg-black text-[#FFCC00]" data-testid="invoice-email-send">
                        {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                        {files.length > 0 ? `Senden (+${files.length} Anhang/Anhänge)` : "Senden"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
